import { Alert, Button, Stack, Typography } from '@pulse/ui';
import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

// Catches render errors anywhere below it — most importantly a failed dynamic import of a
// lazy-loaded route chunk (a transient network blip, or a stale chunk hash after a new deploy),
// which otherwise crashes the whole SPA to a blank screen with no recovery besides a manual
// refresh the user has to think to do themselves.
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('Unhandled render error:', error, info.componentStack);
  }

  render() {
    if (this.state.error) {
      return (
        <Stack spacing={2} alignItems="center" justifyContent="center" sx={{ minHeight: '100vh', p: 3, textAlign: 'center' }}>
          <Alert severity="error" sx={{ maxWidth: 480 }}>
            Something went wrong loading this page. This is usually a temporary network issue.
          </Alert>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 480, wordBreak: 'break-word' }}>
            {this.state.error.message}
          </Typography>
          <Button variant="contained" onClick={() => window.location.reload()}>Reload</Button>
        </Stack>
      );
    }
    return this.props.children;
  }
}
